import { ActionIcon, Button, Group, ScrollArea, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconCheck, IconDeviceDesktopDown, IconHandStop, IconMicrophone, IconMicrophoneOff, IconX } from '@tabler/icons-react';
import { useIsMuted, useParticipantInfo, useParticipants } from '@livekit/components-react';
import { type Participant, Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { type Lobby, type LobbyRequest, useLobby } from '../lobby/lobby';
import { useIsRecordingLocally } from '../local-recording';
import { useIsHandRaised, useRaisedHands } from '../reactions';
import { participantName } from './participant-name';
import { RemoveGuestMenu } from './remove-guest-menu';
import { SidePanel } from './side-panel';
import { UserAvatar } from '@/shared/user-avatar';

export function ParticipantsPanel({ onClose }: Readonly<{ onClose: () => void }>) {
    const { t } = useTranslation('conference');
    const participants = useParticipants();
    const raisedHands = useRaisedHands();
    const lobby = useLobby();

    return (
        <SidePanel closeLabel={t('participants.close')} title={t('participants.title')} onClose={onClose}>
            <ScrollArea flex={1} mih={0}>
                <Stack gap="var(--spacing-4)">
                    {lobby && lobby.requests.length > 0 && <LobbySection lobby={lobby} />}
                    {raisedHands.length > 0 && (
                        <ParticipantSection participants={raisedHands} title={t('participants.raisedHands')} />
                    )}
                    <ParticipantSection participants={participants} title={t('participants.inCall')} />
                </Stack>
            </ScrollArea>
        </SidePanel>
    );
}

function LobbySection({ lobby }: Readonly<{ lobby: Lobby }>) {
    const { t } = useTranslation('conference');
    return (
        <Stack gap="var(--spacing-0-5)">
            <Group justify="space-between" px="var(--spacing-3)" wrap="nowrap">
                <Text c="dimmed" fw={500} size="xs">
                    {t('lobby.title')} · {lobby.requests.length}
                </Text>
                <Button size="sm" variant="subtle" onClick={() => void lobby.admitAll()}>
                    {t('lobby.admitAll')}
                </Button>
            </Group>
            {lobby.requests.map((request) => (
                <LobbyRow key={request.guestId} lobby={lobby} request={request} />
            ))}
        </Stack>
    );
}

function LobbyRow({ lobby, request }: Readonly<{ lobby: Lobby; request: LobbyRequest }>) {
    const { t } = useTranslation('conference');
    return (
        <Group gap="var(--spacing-3)" px="var(--spacing-3)" py="var(--spacing-1-5)" wrap="nowrap">
            <UserAvatar name={request.name} size="sm" />
            <Text truncate flex={1} miw={0} size="sm">
                {request.name}
                <Text inherit c="dimmed" component="span">
                    {' '}
                    ({t('lobby.guest')})
                </Text>
            </Text>
            <Tooltip label={t('lobby.admit')}>
                <ActionIcon
                    aria-label={t('lobby.admit')}
                    color="success"
                    size="sm"
                    variant="light"
                    onClick={() => void lobby.admit(request.guestId)}
                >
                    <IconCheck size={16} />
                </ActionIcon>
            </Tooltip>
            <Tooltip label={t('lobby.reject')}>
                <ActionIcon
                    aria-label={t('lobby.reject')}
                    color="danger"
                    size="sm"
                    variant="light"
                    onClick={() => void lobby.reject(request.guestId)}
                >
                    <IconX size={16} />
                </ActionIcon>
            </Tooltip>
        </Group>
    );
}

function ParticipantSection({ title, participants }: Readonly<{ title: string; participants: Participant[] }>) {
    return (
        <Stack gap="var(--spacing-0-5)">
            <Text c="dimmed" fw={500} px="var(--spacing-3)" size="xs">
                {title} · {participants.length}
            </Text>
            {participants.map((participant) => (
                <ParticipantRow key={participant.identity} participant={participant} />
            ))}
        </Stack>
    );
}

function ParticipantRow({ participant }: Readonly<{ participant: Participant }>) {
    const { t } = useTranslation('conference');
    const { name, identity } = useParticipantInfo({ participant });
    const displayName = participantName(name, identity);
    const micMuted = useIsMuted({ participant, source: Track.Source.Microphone });
    const handRaised = useIsHandRaised(participant);
    const recordingLocally = useIsRecordingLocally(participant);
    const micLabel = micMuted ? t('participants.micOff') : t('participants.micOn');

    return (
        <Group gap="var(--spacing-3)" px="var(--spacing-3)" py="var(--spacing-1-5)" wrap="nowrap">
            <UserAvatar name={displayName} size="sm" />
            <Text truncate flex={1} miw={0} size="sm">
                {displayName}
                {participant.isLocal && (
                    <Text inherit c="dimmed" component="span">
                        {' '}
                        ({t('participants.you')})
                    </Text>
                )}
            </Text>
            {recordingLocally && (
                <Tooltip label={t('localRecording.participant')}>
                    <IconDeviceDesktopDown
                        aria-label={t('localRecording.participant')}
                        color="var(--color-warning-default)"
                        size={16}
                    />
                </Tooltip>
            )}
            {handRaised && (
                <Tooltip label={t('participants.handRaised')}>
                    <ThemeIcon
                        aria-label={t('participants.handRaised')}
                        color="success"
                        radius="var(--radius-rounded-full)"
                        size="var(--spacing-6)"
                    >
                        <IconHandStop size={14} />
                    </ThemeIcon>
                </Tooltip>
            )}
            <Tooltip label={micLabel}>
                {micMuted ? (
                    <IconMicrophoneOff aria-label={micLabel} color="var(--color-danger-default)" size={16} />
                ) : (
                    <IconMicrophone aria-label={micLabel} color="var(--color-foreground-muted-default)" size={16} />
                )}
            </Tooltip>
            <RemoveGuestMenu participant={participant} />
        </Group>
    );
}
